import { useState } from 'react'
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { usePermissions } from '@/hooks/useAuth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { 
  Building, 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  Loader2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {Id} from "../../../convex/_generated/dataModel";

const ITEMS_PER_PAGE = 10

export default function CompanyList() {
  const { hasPermission } = usePermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState('')

  // Check permissions
  const canManageCompanies = hasPermission('manage_companies')
  const canViewCompanies = canManageCompanies || hasPermission('view_companies')
  const organizations = useQuery(api.organizations.listOrganizations, canViewCompanies ? {} : 'skip')
  
  // Get companies with pagination
  const {
    results: companies,
    status: companiesStatus,
    loadMore: loadMoreCompanies
  } = usePaginatedQuery(
    api.companies.listCompanies, 
    canViewCompanies ? {} : "skip",
    { initialNumItems: ITEMS_PER_PAGE }
  )

  const hasSearch = Boolean(searchTerm.trim() || organizationFilter)

  // Full dataset for accurate stats and search across all companies
  const allCompanies = useQuery(
    api.companies.listCompanies,
    canViewCompanies ? {} : "skip"
  )
  
  // Delete company mutation
  const deleteCompany = useMutation(api.companies.deleteCompany)

  if (!canViewCompanies) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-8 text-center">
          <Building className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
          <h2 className="mb-2 text-xl font-semibold">Accesso Negato</h2>
          <p className="text-gray-600">Non hai i permessi per consultare le cliniche.</p>
        </CardContent>
      </Card>
    )
  }

  if (companiesStatus === 'LoadingFirstPage') {
    return <Loading />
  }

  if (allCompanies === undefined || organizations === undefined) {
    return <Loading />
  }

  const baseCompanies = hasSearch ? (allCompanies as any[] | undefined) : companies
  const organizationNames = new Map<string, string>(organizations.map((organization) => [organization._id, organization.nome]))
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('it')

  // Filter companies based on search
  const filteredCompanies = (baseCompanies as any)?.filter((company: any) => {
    const matchesOrganization = !organizationFilter || (organizationFilter === 'unassigned'
      ? !company.organization_id
      : company.organization_id === organizationFilter)
    return matchesOrganization && [company.nome, company.suffix, company.email, company.citta, organizationNames.get(company.organization_id)]
      .some((value) => value?.toLocaleLowerCase('it').includes(normalizedSearch))
  }) || []

  const handleDeleteCompany = async (companyId: string) => {
    if (deletingId) return
    if (window.confirm('Sei sicuro di voler eliminare questa clinica?')) {
      setDeletingId(companyId)
      setDeleteError('')
      try {
        await deleteCompany({ companyId: companyId as Id<"companies">})
      } catch (error) {
        console.error('Errore eliminazione clinica:', error)
        setDeleteError('Non è stato possibile eliminare la clinica. Riprova.')
      } finally {
        setDeletingId(null)
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-gray-950">Cliniche</h1>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Gestisci le cliniche e la loro società di appartenenza
            </p>
          </div>
        </div>
        {canManageCompanies && (
          <Button asChild className="w-full sm:w-auto">
            <Link to="/companies/new">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Nuova clinica
            </Link>
          </Button>
        )}
      </div>

      {deleteError && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{deleteError}</p>}
      {/* Superficie operativa cliniche */}
      <Card className="overflow-hidden shadow-none">
        <dl className="grid grid-cols-2 bg-gray-50/70 sm:grid-cols-3">
          <div className="border-b border-r border-gray-200 px-4 py-3 sm:border-b-0">
            <dt className="text-xs font-medium text-gray-500">Totale cliniche</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allCompanies as any)?.length || 0}
            </dd>
          </div>
          <div className="border-b border-gray-200 px-4 py-3 sm:border-b-0 sm:border-r">
            <dt className="text-xs font-medium text-gray-500">Cliniche attive</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allCompanies as any)?.filter((c: any) => !c.is_deleted).length || 0}
            </dd>
          </div>
          <div className="col-span-2 px-4 py-3 sm:col-span-1">
            <dt className="text-xs font-medium text-gray-500">Risultati ricerca</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {filteredCompanies.length}
            </dd>
          </div>
        </dl>

        <div className="border-y border-gray-200 px-4 py-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="w-full xl:max-w-xl">
              <label htmlFor="company-search" className="mb-1.5 block text-xs font-medium text-gray-600">
                Cerca
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <Input
                  id="company-search"
                  placeholder="Nome clinica, suffisso, società, email o città"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full xl:max-w-xs">
              <label htmlFor="company-organization-filter" className="mb-1.5 block text-xs font-medium text-gray-600">Società</label>
              <select id="company-organization-filter" value={organizationFilter} onChange={(event) => setOrganizationFilter(event.target.value)} className="flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                <option value="">Tutte le società</option>
                <option value="unassigned">Senza società collegata</option>
                {organizations.map((organization) => <option key={organization._id} value={organization._id}>{organization.nome}</option>)}
              </select>
            </div>
            <p className="flex min-h-10 items-center text-sm text-gray-600" aria-live="polite">
              <span className="font-semibold text-gray-950 tabular-nums">{filteredCompanies.length}</span>
              <span className="ml-1">risultati</span>
              {!hasSearch && companiesStatus === 'CanLoadMore' && (
                <span className="ml-1 text-gray-500">· altri disponibili</span>
              )}
            </p>
          </div>
        </div>

        {filteredCompanies.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Building className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-semibold text-gray-950">Nessuna clinica trovata</h3>
            <p className="mx-auto mt-1 max-w-lg text-sm leading-6 text-gray-600">
              {hasSearch ? 'Prova a modificare la ricerca o il filtro società.' : 'Inizia creando la prima clinica.'}
            </p>
            {!hasSearch && canManageCompanies && (
              <Button asChild className="mt-4">
                <Link to="/companies/new">
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Crea prima clinica
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">Cliniche del sistema</caption>
            <thead className="hidden bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500 lg:table-header-group">
              <tr>
                <th scope="col" className="w-[24%] px-4 py-3">Clinica e società</th>
                <th scope="col" className="px-3 py-3">Contatti e sede</th>
                <th scope="col" className="w-44 px-3 py-3">Stato e attività</th>
                <th scope="col" className="w-28 px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="block divide-y divide-gray-200 lg:table-row-group">
              {filteredCompanies.map((company: any) => {
                const hasContacts = Boolean(company.indirizzo || company.telefono || company.email)

                return (
                  <tr key={company._id} className="block bg-white transition-colors hover:bg-gray-50/70 lg:table-row">
                    <th scope="row" className="block px-4 pb-2 pt-4 text-left font-normal lg:table-cell lg:px-4 lg:py-3">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <p className="break-words font-semibold leading-5 text-gray-950">{company.nome}</p>
                        {company.suffix && <span className="break-all text-sm text-gray-600" aria-label={`Suffisso ${company.suffix}`}>({company.suffix})</span>}
                      </div>
                      {company.organization_id && organizationNames.has(company.organization_id) ? (
                        <Link to={`/organizations/${company.organization_id}/edit`} className="mt-1 inline-block break-words text-sm text-gray-600 underline underline-offset-4 hover:text-gray-950 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary" aria-label={`Apri società ${organizationNames.get(company.organization_id)}`}>
                          {organizationNames.get(company.organization_id)}
                        </Link>
                      ) : <p className="mt-1 text-sm text-gray-500">{company.organization_id ? 'Società non disponibile' : 'Senza società collegata'}</p>}
                    </th>

                    <td className="block px-4 py-2 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mb-1.5 block text-xs font-medium text-gray-500 lg:hidden">Contatti e sede</span>
                      {hasContacts ? (
                        <div className="space-y-1.5 text-sm text-gray-600">
                          {company.indirizzo && (
                            <div className="flex min-w-0 items-start gap-2">
                              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                              <span className="min-w-0 break-words">
                                {company.indirizzo}
                                {company.citta && `, ${company.citta}`}
                                {company.cap && ` ${company.cap}`}
                                {company.provincia && ` (${company.provincia})`}
                              </span>
                            </div>
                          )}
                          {company.telefono && (
                            <div className="flex min-w-0 items-center gap-2">
                              <Phone className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                              <span className="break-words">{company.telefono}</span>
                            </div>
                          )}
                          {company.email && (
                            <div className="flex min-w-0 items-center gap-2">
                              <Mail className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                              <span className="min-w-0 break-all" title={company.email}>{company.email}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    <td className="block px-4 py-2 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mb-1.5 block text-xs font-medium text-gray-500 lg:hidden">Stato e attività</span>
                      <Badge className={company.is_deleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                        {company.is_deleted ? 'Eliminata' : 'Attiva'}
                      </Badge>
                      <p className="mt-1.5 text-sm text-gray-500 tabular-nums">
                        Creata: {new Date(company.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </td>

                    <td className="block px-4 pb-4 pt-3 lg:table-cell lg:px-4 lg:py-3">
                      {canManageCompanies && (
                        <div className="flex items-center gap-2 lg:justify-end">
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-11 flex-1 px-3 lg:h-9 lg:w-9 lg:flex-none lg:px-0"
                          >
                            <Link
                              to={`/companies/${company._id}/edit`}
                              aria-label={`Modifica ${company.nome}`}
                              title="Modifica clinica"
                            >
                              <Edit className="h-4 w-4" aria-hidden="true" />
                              <span className="ml-2 lg:sr-only">Modifica</span>
                            </Link>
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteCompany(company._id)}
                            disabled={deletingId !== null}
                            className="h-11 flex-1 px-3 text-red-700 hover:text-red-800 lg:h-9 lg:w-9 lg:flex-none lg:px-0"
                            aria-label={`Elimina ${company.nome}`}
                            title="Elimina clinica"
                          >
                            {deletingId === company._id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                            <span className="ml-2 lg:sr-only">Elimina</span>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {!hasSearch && (companiesStatus === 'CanLoadMore' || companiesStatus === 'LoadingMore') && (
          <div className="flex justify-center border-t border-gray-200 px-4 py-4">
            <Button
              variant="outline"
              onClick={() => companiesStatus === 'CanLoadMore' && loadMoreCompanies(ITEMS_PER_PAGE)}
              disabled={companiesStatus === 'LoadingMore'}
              className="min-h-11 w-full sm:w-auto sm:min-w-[200px]"
            >
              {companiesStatus === 'LoadingMore' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ChevronDown className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {companiesStatus === 'LoadingMore' ? 'Caricamento...' : 'Carica altre cliniche'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
